/**
 * Repository for Signal Management
 * 
 * Handles CRUD operations for workflow signals
 */

import { Signal } from '../types/WorkflowEntities';
import BaseRepository, { FilterParams, PaginationParams, SortParams } from './BaseRepository';

export class SignalRepository extends BaseRepository<Signal> {

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Signal | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Signal',
            criticalityLevel: 'HIGH', // Signals are critical for workflow communication
            metadata: { id, signalRepository: true }
        });

        const query = `
            query GetSignalByTenantAndId($id: String!, $tenant: String!) {
                getSignal(id: $id, tenant: $tenant) {
                    id name description schema status
                    instanceId payload metadata
                    createdAt updatedAt
                }
            }
        `;

        try {
            const result = await this.query<{ getSignal: Signal | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 30 * 1000 // 30 seconds cache for signals
            });

            const signal = result.getSignal || null;

            // ✅ FERP SAFETY: Log successful operation with signal details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Signal',
                    entityId: id,
                    found: !!signal,
                    signalStatus: signal?.status,
                    signalInstanceId: signal?.instanceId,
                    timestamp: Date.now()
                });
            }

            return signal;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Signal',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(signalId: string): Promise<Signal | null> {
        return this.findByTenantAndId(this.tenant, signalId);
    }

    /**
     * Find all signals with optional filtering
     */
    async findAll(params?: {
        filter?: FilterParams;
        pagination?: PaginationParams;
        sort?: SortParams;
    }): Promise<Signal[]> {
        const query = `
            query ListSignals($tenant: String!, $filter: SignalFilter, $pagination: PaginationInput, $sort: SortInput) {
                listSignals(tenant: $tenant, filter: $filter, pagination: $pagination, sort: $sort) {
                    items {
                        id name description schema status
                        instanceId payload metadata
                        createdAt updatedAt
                    }
                    total hasNext
                }
            }
        `;

        const response = await this.query(
            query,
            {
                tenant: this.tenant,
                filter: params?.filter,
                pagination: params?.pagination || { offset: 0, limit: 50 },
                sort: params?.sort
            },
            {
                cache: true,
                cacheTime: 30000 // 30 seconds
            }
        );

        return response.listSignals?.items || [];
    }

    /**
     * Find a single signal by ID
     */
    async findOne(id: string): Promise<Signal | null> {
        const query = `
            query GetSignal($tenant: String!, $id: String!) {
                getSignal(tenant: $tenant, id: $id) {
                    id name description schema status
                    instanceId payload metadata
                    createdAt updatedAt
                }
            }
        `;

        const response = await this.query(
            query,
            { tenant: this.tenant, id },
            {
                cache: true,
                cacheTime: 60000 // 1 minute
            }
        );

        return response.getSignal || null;
    }

    /**
     * Send a signal
     */
    async sendSignal(signalData: {
        instanceId: string;
        signalName: string;
        payload?: any;
        metadata?: any;
    }): Promise<Signal> {
        const mutation = `
            mutation SendSignal($tenant: String!, $input: SendSignalInput!) {
                sendSignal(tenant: $tenant, input: $input) {
                    id name status createdAt
                }
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                input: signalData
            },
            {
                onSuccess: () => {
                    this.cache.invalidate('ListSignals');
                }
            }
        );

        return response.sendSignal;
    }

    // Alias expected by some hooks
    async send(input: { instanceId: string; signalName: string; payload?: any; metadata?: any; }): Promise<Signal> {
        return this.sendSignal(input);
    }

    /**
     * Create a signal definition
     */
    async create(data: Partial<Signal>): Promise<Signal> {
        const mutation = `
            mutation CreateSignal($tenant: String!, $input: CreateSignalInput!) {
                createSignal(tenant: $tenant, input: $input) {
                    id name description schema status
                    createdAt updatedAt
                }
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                input: data
            },
            {
                onSuccess: () => {
                    this.cache.invalidate('ListSignals');
                }
            }
        );

        return response.createSignal;
    }

    /**
     * Update signal
     */
    async update(id: string, data: Partial<Signal>): Promise<Signal> {
        const mutation = `
            mutation UpdateSignal($tenant: String!, $id: String!, $input: UpdateSignalInput!) {
                updateSignal(tenant: $tenant, id: $id, input: $input) {
                    id name description schema status
                    updatedAt
                }
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                id,
                input: data
            },
            {
                onSuccess: () => {
                    this.cache.invalidate(`GetSignal:${id}`);
                    this.cache.invalidate('ListSignals');
                }
            }
        );

        return response.updateSignal;
    }

    /**
     * Delete signal
     */
    async delete(id: string): Promise<boolean> {
        const mutation = `
            mutation DeleteSignal($tenant: String!, $id: String!) {
                deleteSignal(tenant: $tenant, id: $id)
            }
        `;

        const response = await this.mutation(
            mutation,
            {
                tenant: this.tenant,
                id
            },
            {
                onSuccess: () => {
                    this.cache.invalidate(`GetSignal:${id}`);
                    this.cache.invalidate('ListSignals');
                }
            }
        );

        return response.deleteSignal;
    }

    /**
     * Get signals for instance
     */
    async findByInstanceId(instanceId: string): Promise<Signal[]> {
        return this.findAll({
            filter: { instanceId }
        });
    }
}

// Export singleton instance
export const signalRepository = new SignalRepository();

export default SignalRepository;